import { useLang } from 'hooks/lang';

import { Recruitment, User } from 'entities';

import { Button } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { useRecruitmentOwnerChange } from '../hooks';

type BaseRecruitment = Pick<Recruitment, 'status' | 'id'> & {
  owner: User;
};

interface RecruitmentOwnerProps<RecruitmentEntity extends BaseRecruitment> {
  children?: never;
  recruitment: RecruitmentEntity;
  updateRecruitment: (data: RecruitmentEntity) => void;
  showName?: boolean;
  allowChange?: boolean;
  size?: number;
}

export function RecruitmentOwner<RecruitmentEntity extends BaseRecruitment>({
  recruitment,
  updateRecruitment,
  showName = true,
  allowChange = true,
  size,
}: RecruitmentOwnerProps<RecruitmentEntity>): JSX.Element {
  const { t } = useLang();

  const { canChangeRecruitmentOwner, changeRecruitmentOwner, loading } = useRecruitmentOwnerChange({
    recruitment,
    onNewOwner: (owner) => updateRecruitment({ ...recruitment, owner }),
  });

  if (!allowChange) return <UserAvatar user={recruitment.owner} name={showName} />;

  const canChange = canChangeRecruitmentOwner(recruitment);

  return (
    <Button
      variant="ghost"
      disabled={!canChange}
      onClick={changeRecruitmentOwner}
      loading={loading}
      className="disabled:opacity-1 mx-auto flex w-fit min-w-fit p-0 py-0.5 font-normal text-inherit"
      tooltipContent={canChange ? t('change') : undefined}
    >
      <UserAvatar user={recruitment.owner} name={showName} size={size} />
    </Button>
  );
}
